import { csvText, type Family, type FamilyGroup, type OwnerParticipant } from "@teamna/shared";
import { childGroupText, familyHasNumber, familyName, isPlaced, phoneText, type Words } from "./model.ts";

/**
 * The Parents list, as data (owner menu revamp, 2026-10-09). Two reads make one row per family:
 * the families (who the adults and the children are, the invite, the hold) and the participants
 * (each child's group, balance and paid or owed). Pure functions, so the filters and the export are
 * tested without a screen.
 */

/** A balance at or under this is "low": the family needs a new package soon. */
export const LOW_SESSIONS = 2;

export type ParentRow = {
  family: Family;
  name: string;
  /** The participant record of each child, in the family's order. A child the list does not know yet is left out. */
  participants: OwnerParticipant[];
  /**
   * The family's sessions left. Each child reads the family's balance (decision 2026-10-04), so
   * this is the largest one, not the sum. Null when no child has a package yet.
   */
  sessionsLeft: number | null;
  /** "owed" when any package is not paid, "paid" when every package is paid, null with no package. */
  payment: "owed" | "paid" | null;
  /** A membership month with no limit pays: no count to show. */
  unlimited: boolean;
  low: boolean;
  /**
   * A child the participants read does not list yet (a stale read). The balance and paid or owed
   * then miss that child, so the table says "Not ready" rather than a number that may be wrong.
   */
  notReady: boolean;
  lastActivityAt: string | null;
};

export function buildParentRows(families: Family[], participants: OwnerParticipant[]): ParentRow[] {
  const byId = new Map(participants.map((participant) => [participant.id, participant]));
  return families.map((family) => {
    const own = family.children
      .map((child) => byId.get(child.participantId))
      .filter((participant): participant is OwnerParticipant => participant !== undefined);
    const withPackage = own.filter((participant) => participant.packageName !== null);
    const sessionsLeft = withPackage.length ? Math.max(...withPackage.map((participant) => participant.sessionsLeft)) : null;
    const unlimited = withPackage.some((participant) => participant.unlimited === true);
    const payment = own.some((participant) => participant.paymentStatus === "owed")
      ? "owed"
      : own.some((participant) => participant.paymentStatus === "paid")
        ? "paid"
        : null;
    const lastActivityAt = own.reduce<string | null>((latest, participant) => {
      const at = participant.lastActivityAt ?? null;
      return at && (!latest || at > latest) ? at : latest;
    }, null);
    return {
      family,
      name: familyName(family),
      participants: own,
      sessionsLeft,
      payment,
      unlimited,
      low: !unlimited && sessionsLeft !== null && sessionsLeft <= LOW_SESSIONS && family.holdNote === null,
      notReady: own.length < family.children.length,
      lastActivityAt,
    };
  });
}

export type ParentFilter = "all" | "owes" | "low" | "notjoined" | "nogroup" | "nophone" | "onhold";

export function parentFilterTest(filter: ParentFilter, groups: FamilyGroup[]): (row: ParentRow) => boolean {
  switch (filter) {
    // A row that is not ready may owe or be low through the child it misses: it stays in these
    // lists, where its "Not ready" cells say why, rather than drop out unseen.
    case "owes":
      return (row) => row.payment === "owed" || row.notReady;
    case "low":
      return (row) => row.low || row.notReady;
    case "notjoined":
      return (row) => row.family.invite !== "joined";
    case "nogroup":
      return (row) => row.family.children.some((child) => !isPlaced(child, groups));
    case "nophone":
      return (row) => !familyHasNumber(row.family);
    case "onhold":
      return (row) => row.family.holdNote !== null;
    default:
      return () => true;
  }
}

const INVITE_WORDS = { none: "Not invited", invited: "Invited", joined: "Joined" } as const;

/** The export: one line per family, in the business's words. Numbers are full: the owner typed them. */
export function parentsCsv(rows: ParentRow[], groups: FamilyGroup[], words: Words): string {
  const capital = (word: string) => (word ? word[0]!.toUpperCase() + word.slice(1) : word);
  const header = [capital(words.member), "Mobile number", capital(words.participants), capital(words.group), "Sessions left", "Paid or owed", "Invite", "On hold"];
  const lines = rows.map((row) => [
    row.name,
    row.family.adults
      .filter((adult) => adult.phoneE164)
      .map((adult) => phoneText(adult.phoneE164))
      .join("; "),
    row.family.children.map((child) => child.name).join("; "),
    row.family.children.map((child) => childGroupText(child, groups, words)).join("; "),
    row.notReady ? "Not ready" : row.unlimited ? "No limit" : (row.sessionsLeft ?? ""),
    row.notReady ? "Not ready" : row.payment === "owed" ? "Owed" : row.payment === "paid" ? "Paid" : "",
    INVITE_WORDS[row.family.invite],
    row.family.holdNote ?? "",
  ]);
  return csvText(header, lines);
}

